import { useEffect, useMemo } from 'react';
import { useLazyQuery, ApolloError } from '@apollo/client';
import { getClient } from 'lane-shared/apollo';
import { useMultiLanguage } from 'lane-shared/hooks';
import { ContentQueryInteraction } from 'lane-shared/hooks/useContentQuery';
import { PublicUserFragment } from 'lane-shared/graphql/fragments';

import { buildSearchParams } from '../utils/searchParams';
import { SortDirectionEnum } from 'constants-activate';
import { DEFAULT_PAGINATION } from '../constants';
import gql from 'graphql-tag';

const interactionsOnContentQuery = gql`
  ${PublicUserFragment}

  query interactionsOnContent(
    $contentId: UUID!
    $search: UserContentInteractionSearch
    $pagination: PaginationInput
    $forAdmin: Boolean
  ) {
    interactionsOnContent(
      contentId: $contentId
      search: $search
      pagination: $pagination
      forAdmin: $forAdmin
    ) {
      pageInfo {
        total
        start
        perPage
      }
      items {
        _id
        _created
        _updated
        _createdBy {
          ...PublicUserFragment
        }
        _updatedBy {
          ...PublicUserFragment
        }
        user {
          _id
          name
          profile {
            _id
            name
            email
            image
            logo
          }
        }
        channel {
          _id
        }
        startDate
        endDate
        geo
        data
        state
        features
        actions
        contentData
        version
        status
        lastStatus
      }
    }
  }
`;

export interface FilterState {
  sortBy: string;
  sortDirection: SortDirectionEnum;
  bookingDateRange: { startDate?: Date; endDate?: Date };
  reservationDateRange: { startDate?: Date; endDate?: Date };
}

interface UseInteractionDataProps {
  contentId: string;
  filters: FilterState;
  isReservableContent: boolean;
  initialInteractions: ContentQueryInteraction[];
}

interface UseInteractionDataReturn {
  interactions: ContentQueryInteraction[];
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
}

export function useInteractionData({
  contentId,
  filters,
  isReservableContent,
  initialInteractions,
}: UseInteractionDataProps): UseInteractionDataReturn {
  const { translate } = useMultiLanguage();

  const canQuery = !!contentId;

  const searchParams = useMemo(() => {
    return buildSearchParams(filters, isReservableContent);
  }, [filters, isReservableContent]);

  const variables = useMemo(
    () => ({
      contentId,
      search: {
        ...searchParams,
      },
      pagination: DEFAULT_PAGINATION,
      forAdmin: false,
    }),
    [contentId, searchParams]
  );

  const [fetchInteractions, { data, loading, error, called }] = useLazyQuery(
    interactionsOnContentQuery,
    {
      fetchPolicy: 'network-only',
      client: getClient(),
      variables,
    }
  );

  useEffect(() => {
    if (canQuery) {
      fetchInteractions({ variables });
    }
  }, [canQuery, variables, fetchInteractions]);

  const interactions = useMemo(() => {
    const items =
      data?.interactionsOnContent?.items || initialInteractions || [];

    return items.map((item: any) => ({
      ...item,
      contentData: translate({
        model: item?.contentData,
        columns: ['name', 'description', 'subtitle'],
      }),
    }));
  }, [data?.interactionsOnContent?.items, initialInteractions, translate]);

  const refetch = () => {
    if (canQuery) {
      fetchInteractions({ variables });
    }
  };

  return {
    interactions,
    loading: canQuery ? loading || !called : false,
    error,
    refetch,
  };
}
